/**
 * Centralized reader for environment variables.
 * All config lives here so modules never read `import.meta.env` directly.
 */
import { HISTORICAL_IMPORT_ALLOWED_USERS } from "./historicalImportAllowlist";
import { BILLS_REGISTRY_ALLOWED_USERS } from "./billsRegistryAllowlist";
import { SUP_APPROVAL_ALLOWED_USERS } from "./supApprovalAllowlist";
import { optionalExternalUrl } from "./externalUrl";

function required(name: string, value: string | undefined): string {
  if (!value || value.startsWith("PASTE_")) {
    throw new Error(
      `Missing required env var: ${name}. Check your .env.local file (or SWA Application Settings in production).`
    );
  }
  return value;
}

import { builtAt } from "./builtAt";

export const appConfig = {
  appName: import.meta.env.VITE_APP_NAME ?? "i-SYNERGY",
  appVersion: import.meta.env.VITE_APP_VERSION ?? "0.1.0",
  /** When this bundle was built. "" when unknown — see builtAtLabel. */
  builtAt: builtAt(),

  /**
   * E.V.A assistant on/off switch. Disabled by default — the floating assistant
   * only mounts when VITE_EVA_ENABLED is truthy ("true"/"1"/"yes"/"on"). Flip it
   * in the SWA Application Settings (then redeploy) to re-enable, no code change.
   */
  evaEnabled: /^(true|1|yes|on)$/i.test(String(import.meta.env.VITE_EVA_ENABLED ?? "")),

  auth: {
    tenantId: required("VITE_AAD_TENANT_ID", import.meta.env.VITE_AAD_TENANT_ID),
    clientId: required("VITE_AAD_CLIENT_ID", import.meta.env.VITE_AAD_CLIENT_ID),
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AAD_TENANT_ID}`,
    redirectUri: window.location.origin
  },

  sharepoint: {
    siteUrl: required(
      "VITE_SHAREPOINT_SITE_URL",
      import.meta.env.VITE_SHAREPOINT_SITE_URL
    ),
    resource: required(
      "VITE_SHAREPOINT_RESOURCE",
      import.meta.env.VITE_SHAREPOINT_RESOURCE
    )
  },

  /**
   * M365 emails allowed to import historical order data. Defaults to the IG OPC
   * employee roster; set VITE_HISTORICAL_IMPORT_ALLOWED_USERS (comma-separated)
   * to override without a code change.
   */
  historicalImportAllowedUsers: import.meta.env.VITE_HISTORICAL_IMPORT_ALLOWED_USERS
    ? import.meta.env.VITE_HISTORICAL_IMPORT_ALLOWED_USERS
        .split(",")
        .map((e: string) => e.trim().toLowerCase())
        .filter(Boolean)
    : HISTORICAL_IMPORT_ALLOWED_USERS,

  /**
   * M365 emails allowed to open the Bills & Subscriptions module at all (not
   * just one feature) — its data is confidential, restricted to the owner,
   * HR, and Accounting. Set VITE_BILLS_REGISTRY_ALLOWED_USERS (comma-separated)
   * to override without a code change.
   */
  billsRegistryAllowedUsers: import.meta.env.VITE_BILLS_REGISTRY_ALLOWED_USERS
    ? import.meta.env.VITE_BILLS_REGISTRY_ALLOWED_USERS
        .split(",")
        .map((e: string) => e.trim().toLowerCase())
        .filter(Boolean)
    : BILLS_REGISTRY_ALLOWED_USERS,

  /**
   * M365 emails allowed to act on BQC5's Sup Approval tab — the sign-off on
   * an in-house repair (see supApprovalAllowlist.ts). Set
   * VITE_SUP_APPROVAL_ALLOWED_USERS (comma-separated) to override without a
   * code change.
   */
  supApprovalAllowedUsers: import.meta.env.VITE_SUP_APPROVAL_ALLOWED_USERS
    ? import.meta.env.VITE_SUP_APPROVAL_ALLOWED_USERS
        .split(",")
        .map((e: string) => e.trim().toLowerCase())
        .filter(Boolean)
    : SUP_APPROVAL_ALLOWED_USERS,

  /**
   * The fulfillment team's ERP — A SEPARATE APPLICATION, not a module of
   * this one: Flask and PostgreSQL, its own login, its own roles, running on
   * the fulfillment team's own machine. It covers BQC6 to BQC10 (inbound,
   * outbound, packaging, drop-off, returns) plus the order database, and it
   * CONSUMES BQC1 to BQC5, which are processed here.
   *
   * NOT wired to a Home tile any more (2026-09-24) — the Fulfillment tile
   * now lands straight on the ported mirror's own Home (ErpHome,
   * /fulfillment/home) instead of a drawer offering a route in this app or
   * an external link to theirs, since the mirror covers most of their
   * sidebar now rather than just Order Database. Kept here, unread, as the
   * one place their real hostname is still recorded for whoever next needs
   * to link out to the live app itself.
   *
   * Accepts either an https hostname or a plain-http LAN address such as
   * http://RECEIVING-PC:5000, because the ERP's own server_ip.py publishes
   * exactly that form to the office network. See optionalExternalUrl for
   * which hosts may skip https and why a public one may not.
   */
  fulfillmentErpUrl: optionalExternalUrl(import.meta.env.VITE_FULFILLMENT_ERP_URL)
} as const;
